import { useState, useEffect, useRef, useCallback } from 'react'
import { MessageSquare, Send, RefreshCw, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Card, CardContent } from '@/components/ui/card'
import { lawyerCasesAPI } from '@/utils/api'

interface Message {
  id: number
  sender: number
  sender_name: string
  sender_type: 'client' | 'lawyer'
  message_text: string
  is_read: boolean
  created_at: string
}

interface LawyerChatTabProps {
  caseId: number
  onMessagesRead?: () => void
}

export function LawyerChatTab({ caseId, onMessagesRead }: LawyerChatTabProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const hasMarkedRead = useRef(false)

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true)
      const response = await lawyerCasesAPI.getMessages(caseId)
      if (response.status === 'success') {
        setMessages(response.messages || [])
      }
    } catch (err) {
      console.error('Error fetching messages:', err)
      toast.error('Nachrichten konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [caseId])

  // Mark messages as read on initial load (once per tab open)
  useEffect(() => {
    if (caseId && !hasMarkedRead.current) {
      lawyerCasesAPI
        .markMessagesRead(caseId)
        .then(() => {
          hasMarkedRead.current = true
          onMessagesRead?.()
        })
        .catch(console.error)
    }
  }, [caseId, onMessagesRead])

  useEffect(() => {
    if (caseId) {
      fetchMessages()
    }
  }, [caseId, fetchMessages])

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]')
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight
      }
    }
  }, [messages])

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return

    setSending(true)
    try {
      const response = await lawyerCasesAPI.sendMessage(caseId, newMessage.trim())
      if (response.status === 'success') {
        toast.success('Nachricht gesendet')
        setNewMessage('')
        fetchMessages()
      }
    } catch (err) {
      console.error('Error sending message:', err)
      toast.error('Nachricht konnte nicht gesendet werden')
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-slate-400" />
          Mandanten-Kommunikation ({messages.length})
        </h2>
        <Button onClick={fetchMessages} size="sm" variant="ghost">
          <RefreshCw className="h-4 w-4 mr-1" />
          Aktualisieren
        </Button>
      </div>

      {/* Messages */}
      <ScrollArea className="h-[400px] p-4" ref={scrollAreaRef}>
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-500">
            <MessageSquare className="h-12 w-12 text-slate-300 mb-2" />
            <p className="text-sm">Noch keine Nachrichten</p>
            <p className="text-xs text-slate-400 mt-1">
              Hier erscheinen Nachrichten vom Mandanten
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => {
              const isLawyer = msg.sender_type === 'lawyer'
              return (
                <Card
                  key={msg.id}
                  className={isLawyer ? 'ml-[15%] bg-blue-50/50' : 'mr-[15%] bg-white'}
                >
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={isLawyer ? 'default' : 'secondary'}
                          className={isLawyer ? 'bg-blue-600' : ''}
                        >
                          {isLawyer ? 'Anwalt' : 'Mandant'}
                        </Badge>
                        <span className="text-xs font-medium text-slate-700">
                          {msg.sender_name}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">{formatDate(msg.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.message_text}</p>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </ScrollArea>

      {/* Send message form */}
      <div className="p-4 border-t">
        <div className="space-y-3">
          <Textarea
            placeholder="Nachricht eingeben..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
            className="resize-none"
          />
          <div className="flex justify-between items-center">
            <span className="text-xs text-slate-400">Cmd+Enter zum Senden</span>
            <Button onClick={handleSendMessage} disabled={sending || !newMessage.trim()}>
              {sending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Senden
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
